/**
 * Ein Eintrag im Raster (`kal-eintrag-<id>`, Spec 07 „Ein Eintrag", Spec 45) —
 * Symbol, Titel, bei `kind = charge` rechtsbündig der Betrag. **Die Art wird
 * ausschließlich über das Symbol getragen, nie über Farbe** (AK-458): Die
 * einzige Farbe an dieser Zeile ist der 3px-Rand bei `is_overdue`, unabhängig
 * von `kind`. Termine und Fristen liegen zusätzlich als Balken (`bg-accent/10`)
 * unter dem Titel — das ist Fläche, kein Statuswert (Entwurf Variante A).
 *
 * Termin, Frist und Sollstellung führen dorthin, wo sie leben
 * (`entry-target.ts`, AK-466): auf den Reiter der Objektansicht oder bei einer
 * gebündelten Mietfälligkeit in den Bereich *Geld*; drei `<Link>`-Zweige statt
 * eines generischen `to`, weil TanStack Routers typisierte Props einen
 * literalen Pfad verlangen. Ein Eintrag mit maskiertem Subjekt (`type:
 * 'none'`) bekommt keinen `<Link>` — er ist absichtlich nicht klickbar.
 *
 * **Aufgaben** (Spec 45) sind keine Links: Ein Ring-Knopf `task-done-<id>`
 * (Ticket-Id, wie `TaskRow`) erledigt sofort (R16), der Titel öffnet das Sheet
 * (`onOpenTask`). Beide stehen nebeneinander — ein Knopf im Knopf wäre
 * ungültiges HTML. Eine erledigte Aufgabe zeigt `CircleCheck`, den Titel
 * durchgestrichen, und keinen Knopf.
 *
 * Zwei Größen: `compact` im Wochenraster (`kal-grid`, ≥ 24px Trefferfläche am
 * Zeiger) und `comfortable` in der Tagesliste (`kal-agenda`, ≥ 44px unter
 * 768px/Touch, mit `detail_display` als zweiter Zeile — im Raster fehlt der
 * Platz) — dieselbe Komponente, kein zweiter Zeilenbau.
 */

import { Banknote, CalendarDays, Circle, CircleCheck, Clock } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { ComponentType } from 'react'

import { calendarEntryTarget } from '@/features/calendar/entry-target'
import type { CalendarEntry } from '@/features/calendar/api'
import { toKalenderView } from '@/lib/kalender-view'

const KIND_ICON: Record<string, ComponentType<{ className?: string; 'aria-hidden'?: boolean }>> = {
  charge: Banknote,
  deadline: Clock,
  appointment: CalendarDays,
}

/** Was eine Aufgabenzeile tut — vom Container (`Kalender.tsx`) durch Raster
 * oder Liste gereicht, damit die Zeile selbst keine Mutation besitzt. */
export interface CalendarEntryActions {
  /** Das Statuslabel „Erledigt" aus `GET /tickets/statuses` (Regel „Enum-
   * Labels vom Server"); solange es lädt, trägt der Ring „Erledigt" nicht. */
  readonly doneLabel: string | undefined
  /** Ring: `PATCH /tickets/{id}` `{status: done}` mit `If-Match` (R16). */
  readonly onDone: (entry: CalendarEntry) => void
  readonly onOpenTask: (ticketId: string) => void
}

interface CalendarEntryRowProps {
  readonly entry: CalendarEntry
  readonly actions: CalendarEntryActions
  readonly size?: 'compact' | 'comfortable'
}

export function CalendarEntryRow({ entry, actions, size = 'compact' }: CalendarEntryRowProps) {
  const target = calendarEntryTarget(entry)
  const isTask = entry.kind.value === 'task'
  const isDone = isTask && entry.state.value === 'done'
  const isBar = entry.kind.value === 'appointment' || entry.kind.value === 'deadline'
  const hasAmount = entry.kind.value === 'charge' && entry.amount_display !== null && entry.amount_display !== undefined
  const isComfortable = size === 'comfortable'

  const sizeClass = isComfortable ? 'min-h-11 gap-2 text-body' : 'min-h-6 gap-1 text-label'
  // Das Symbol sitzt in einer Spalte von der Breite des Rings (44px bzw. 24px),
  // damit die Titel aller Arten untereinander bündig stehen.
  const iconColumnClass = isComfortable ? 'min-w-11' : 'min-w-6'

  // `border-l-status` bleibt an jeder Zeile stehen, nur die Farbe wechselt —
  // sonst verschöbe ein überfälliger Eintrag die Titelspalte um 3px gegen
  // seine Nachbarn (dieselbe Bauform wie `OpenItemsHeader`s unsichtbarer
  // Rand-Platzhalter).
  const baseClassName = `border-l-status rounded-card flex items-center px-1 transition-colors duration-(--duration-fast) ease-(--ease-out) ${sizeClass} ${
    entry.is_overdue ? 'border-l-status-overdue' : 'border-l-transparent'
  } ${isBar ? 'bg-accent/10' : ''}`
  // Nur die klickbaren Zweige bekommen den Hover-/Fokus-Zustand — er würde bei
  // `type: 'none'` einen Klick versprechen, den es nicht gibt.
  const hoverClassName = isBar ? 'hover:bg-accent/20 focus-visible:bg-accent/20' : 'hover:bg-bg focus-visible:bg-bg'
  const className = `${baseClassName} ${hoverClassName}`

  const titleClassName = `min-w-0 truncate text-text ${entry.is_overdue ? 'font-semibold' : ''} ${
    isDone ? 'text-muted line-through' : ''
  }`

  const text = (
    <span className="flex min-w-0 flex-1 flex-col text-left">
      <span className={titleClassName}>{entry.title}</span>
      {isComfortable && (
        <span data-testid={`kal-eintrag-detail-${entry.id}`} className="text-label text-muted truncate">
          {entry.detail_display}
        </span>
      )}
    </span>
  )

  const amount = hasAmount && (
    <span data-testid={`kal-eintrag-betrag-${entry.id}`} data-numeric className="shrink-0 text-text">
      {entry.amount_display}
    </span>
  )

  if (isTask) {
    const ticketId = entry.source_id ?? ''
    const KindIcon = isDone ? CircleCheck : Circle
    return (
      <div data-testid={`kal-eintrag-${entry.id}`} className={baseClassName}>
        {isDone ? (
          <span
            data-testid={`kal-eintrag-art-${entry.id}`}
            role="img"
            aria-label={entry.state.label}
            title={entry.state.label}
            className={`text-status-paid flex shrink-0 items-center justify-center ${iconColumnClass}`}
          >
            <KindIcon aria-hidden className="size-3" />
          </span>
        ) : (
          <button
            type="button"
            data-testid={`task-done-${ticketId}`}
            aria-label={actions.doneLabel ?? entry.kind.label}
            title={actions.doneLabel ?? entry.kind.label}
            onClick={() => {
              actions.onDone(entry)
            }}
            // Am Zeiger 24px (WCAG 2.2 2.5.8), unter 768px/Touch 44px.
            className={`text-muted hover:text-accent flex shrink-0 items-center justify-center ${
              isComfortable ? 'min-h-11' : 'h-6'
            } ${iconColumnClass}`}
          >
            {/* Die Art („Aufgabe", AK-458) steht weiter am Symbol; der Knopf selbst
             * heißt „Erledigt". */}
            <KindIcon
              data-testid={`kal-eintrag-art-${entry.id}`}
              role="img"
              aria-label={entry.kind.label}
              className="size-3"
            />
          </button>
        )}
        <button
          type="button"
          title={entry.title}
          onClick={() => {
            actions.onOpenTask(ticketId)
          }}
          className={`hover:bg-bg focus-visible:bg-bg rounded-card flex min-w-0 flex-1 items-center self-stretch ${sizeClass}`}
        >
          {text}
        </button>
      </div>
    )
  }

  const Icon = KIND_ICON[entry.kind.value] ?? CalendarDays
  const content = (
    <>
      <span
        data-testid={`kal-eintrag-art-${entry.id}`}
        role="img"
        aria-label={entry.kind.label}
        title={entry.kind.label}
        className={`text-muted flex shrink-0 items-center justify-center ${iconColumnClass}`}
      >
        <Icon aria-hidden className="size-3" />
      </span>
      {text}
      {amount}
    </>
  )

  if (target.type === 'object') {
    return (
      <Link
        to="/objekt/$entityId"
        params={{ entityId: target.entityId }}
        search={{ tab: target.tab }}
        data-testid={`kal-eintrag-${entry.id}`}
        title={entry.title}
        className={className}
      >
        {content}
      </Link>
    )
  }

  if (target.type === 'contract') {
    return (
      <Link
        to="/"
        // `view` neu gelesen: `previous` ist die Vereinigung aller Routen, `view` heißt
        // bei *Dokumente* etwas anderes.
        search={(previous) => ({ ...previous, view: toKalenderView(previous.view), contract: target.contractId })}
        data-testid={`kal-eintrag-${entry.id}`}
        title={entry.title}
        className={className}
      >
        {content}
      </Link>
    )
  }

  if (target.type === 'geld') {
    return (
      <Link
        to="/geld"
        search={{ overdue: false, tenant: undefined, scope: undefined, tab: 'offene-posten' }}
        data-testid={`kal-eintrag-${entry.id}`}
        title={entry.title}
        className={className}
      >
        {content}
      </Link>
    )
  }

  // `type: 'none'` — maskiertes Subjekt (`entry-target.ts`): kein `<Link>`,
  // absichtlich nicht klickbar statt einer Weiterleitung ins Leere.
  return (
    <div data-testid={`kal-eintrag-${entry.id}`} title={entry.title} className={baseClassName}>
      {content}
    </div>
  )
}
